import { memo } from 'react';
import { AlertTriangle, CheckCircle2, FileArchive, FileText, Loader2, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isArchiveFile, type ItemStatus, type UploadItem } from '../uploadManager';

/** One file of an upload batch — shared by the picker dialog (queued rows) and the tray
 *  (progress, result, and the archive skip / replace detail).
 *
 *  Memoised: a folder upload can be thousands of rows and the tray redraws on every progress
 *  tick. The manager replaces only the item that moved (patchItem keeps every other object),
 *  so with memo a tick re-renders one row instead of the whole list — which is what lets the
 *  tray show EVERY file, scrollable, rather than a capped preview. */
export const UploadFileRow = memo(function UploadFileRow({ item, running, note, onRemove }: {
  item: UploadItem;
  running: boolean;
  /** A pre-upload warning for this row (e.g. its name is already taken at the destination). */
  note?: string;
  onRemove?: () => void;
}) {
  const archive = isArchiveFile(item.file.name);
  return (
    <li className="flex min-w-0 items-center gap-2.5 rounded-md border border-line px-2.5 py-1.5 text-[12.5px]">
      <StatusIcon status={item.status} archive={archive} caution={item.outcome != null} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-navy" title={item.file.name}>{item.file.name}</div>
        {/* The reason wraps, never truncates: it is what the person acts on ("…exceeds the 50 MB
            per-file limit" cut to "…exceeds the 50 MB per-fi" is useless). */}
        {item.status === 'failed' && item.error && (
          <div className="text-[11.5px] leading-snug break-words text-state-error-text">{item.error}</div>
        )}
        {item.status === 'queued' && item.note && (
          <div className="truncate text-[11.5px] text-muted">{item.note}</div>
        )}
        {item.status === 'done' && (item.note || item.documentNumber) && (
          <div className={cn('text-[11.5px] leading-snug break-words',
            item.outcome ? 'text-state-warning-text' : 'truncate text-muted')}>
            {item.note ?? item.documentNumber}
          </div>
        )}
        {item.status === 'done' && item.skipped && item.skipped.length > 0 && (
          <details className="mt-0.5 text-[11.5px]">
            <summary className="cursor-pointer text-state-warning-text hover:underline">
              View {item.skippedCount ?? item.skipped.length} skipped
            </summary>
            <ul className="mt-1 space-y-0.5 border-l border-line pl-2">
              {item.skipped.map((s, i) => (
                <li key={`${s.name}-${i}`} className="text-muted">
                  <span className="break-all text-navy/80">{s.name.split('/').pop() || s.name}</span>
                  {' — '}{s.reason}
                </li>
              ))}
              {(item.skippedCount ?? 0) > item.skipped.length && (
                <li className="text-muted/70">…and {(item.skippedCount ?? 0) - item.skipped.length} more</li>
              )}
            </ul>
          </details>
        )}
        {/* Replacements are listed BY NAME. Each one retired the copy that was in the folder, so
            a bare count gives the user nothing to check — and unlike a skip, this one changed the
            vault. The summary names them so the reader knows what changed — do not call them
            recoverable: nothing in the app restores a retired copy. */}
        {item.status === 'done' && item.replaced && item.replaced.length > 0 && (
          <details className="mt-0.5 text-[11.5px]">
            <summary className="cursor-pointer text-state-warning-text hover:underline">
              View {item.replacedCount ?? item.replaced.length} replaced
            </summary>
            <ul className="mt-1 space-y-0.5 border-l border-line pl-2">
              {item.replaced.map((name, i) => (
                <li key={`${name}-${i}`} className="text-muted">
                  <span className="break-all text-navy/80">{name.split('/').pop() || name}</span>
                  {' — previous version retired'}
                </li>
              ))}
              {(item.replacedCount ?? 0) > item.replaced.length && (
                <li className="text-muted/70">…and {(item.replacedCount ?? 0) - item.replaced.length} more</li>
              )}
            </ul>
          </details>
        )}
        {item.status === 'importing' && (
          <div className="truncate text-[11.5px] text-muted">Unzipping…</div>
        )}
        {item.status === 'queued' && archive && (
          <div className="truncate text-[11.5px] text-muted">Archive · will be unzipped into folders</div>
        )}
        {item.status === 'queued' && note && (
          <div className="truncate text-[11.5px] text-state-warning-text">{note}</div>
        )}
      </div>
      {item.status === 'uploading' && <span className="tabular-nums text-[11.5px] text-muted">{item.pct}%</span>}
      {onRemove && (item.status === 'queued' || item.status === 'failed') && !running && (
        <button
          type="button"
          onClick={onRemove}
          className="text-muted hover:text-navy"
          aria-label={`Remove ${item.file.name}`}
        >
          <X className="size-4" />
        </button>
      )}
    </li>
  );
});

function StatusIcon({ status, archive, caution }: { status: ItemStatus; archive?: boolean; caution?: boolean }) {
  if (status === 'done' && caution) return <AlertTriangle className="size-4 shrink-0 text-state-warning-accent" aria-label="Attention" />;
  if (status === 'done') return <CheckCircle2 className="size-4 shrink-0 text-state-success-accent" aria-label="Done" />;
  if (status === 'failed') return <XCircle className="size-4 shrink-0 text-state-error-accent" aria-label="Failed" />;
  if (status === 'uploading' || status === 'finalizing' || status === 'importing')
    return <Loader2 className="size-4 shrink-0 animate-spin text-electric" aria-label="Working" />;
  return archive
    ? <FileArchive className="size-4 shrink-0 text-navy/60" aria-hidden />
    : <FileText className="size-4 shrink-0 text-muted" aria-hidden />;
}
